<template>
  <div v-if="question">
    <v-card rounded="0" width="100%" elevation="0" class="word-break">
      <div class="d-flex justify-space-between align-center mb-3">
        <Typography variant="body-1">{{ question.label }}</Typography>
        <v-btn
          icon
          small
          color="red"
          class="delete-group-button"
          @click="deleteGroup"
          v-if="!hideDelete"
          ><v-icon>mdi-trash-can-outline</v-icon></v-btn
        >
      </div>
      <div>
        <FormQuestion
          v-for="(child, index) in Questions || []"
          :key="child.formQuestionId"
          :question="child"
          :formId="formId"
          :childQuestions="
            childQuestions.filter((q) => q.parentQuestionId !== question.formQuestionId) || []
          "
          :initialAnswer="answers && answers[child.formQuestionId]"
          :allPrefilledAnswers="answers"
          :validationRules="
            validationRules.filter(
              (x) => x.formQuestionId && x.formQuestionId !== question.formQuestionId
            ) || []
          "
          :apiErrors="apiErrors"
          @update-responses="updateAnswerArray($event, index)"
        ></FormQuestion>
      </div>
    </v-card>
  </div>
</template>

<script>
// Services

// Components
import FormQuestion from "@/components/forms/FormQuestion";
import Typography from "@/components/generic/Typography.vue";

// Packages
import { v4 as uuid } from "uuid";
import { mapState } from "vuex";

export default {
  name: "FormQuestionGroupItem",
  props: {
    question: Object,
    formId: Number,
    childQuestions: {
      type: Array,
      default: () => [],
    },
    validationRules: {
      type: Array,
      default: () => [],
    },
    apiErrors: {
      type: Array,
      default: () => [],
    },
    hideDelete: {
      type: Boolean,
      default: false,
    },
    answers: Object,
  },
  components: { FormQuestion, Typography },
  emits: ["delete-group", "update-responses"],
  data() {
    return {
      out: [],
      uuid: uuid(),
      childAnswers: [],
      textValue: "",
      selectedValue: "",
      formQuestionValueId: null,
      loadingChildQuestion: false,
    };
  },
  created() {
    // We set this from an empty string to null just so the computed Output triggers a change to initialize the answers array
    this.textValue = null;
  },
  updated() {},
  mounted() {},
  beforeMount() {},
  methods: {
    updateAnswerArray(e, i) {
      this.$set(this.childAnswers, i, e);
    },
    deleteGroup() {
      this.$emit("delete-group");
    },
    formatOutput(currentQuestionOutput, children = []) {
      // Children is an array of arrays, so we need to go through each
      // If length is 1 we're going to just flatten it out.
      // The complexity is when a field can be repeated. Then it cascades out and gets applied set of flat questions
      let groupQuestions = (children || []).filter(Boolean);
      let combined = [];
      // Grab simple questions first
      let simpleQuestions = groupQuestions.filter(
        (q) =>
          // If length is 1 then it's not repeated. Then we have to check that element to make sure that it doesn't have any nested values
          q.length === 1 && Array.isArray(q[0]) && q[0].length === 1
      );
      // Grab complex questions - inverse of simple questions
      let complexQuestions = groupQuestions.filter(
        (q) => !(q.length === 1 && Array.isArray(q[0]) && q[0].length === 1)
      );
      // If there are complex questions, we need to combine them with the simple questions

      // Compile simple questions
      let flattenedSimpleQuestions = simpleQuestions.map(
        (q) => q.flat(Infinity).filter(Boolean)[0]
      );

      if (complexQuestions.length > 0) {
        const grouped = this.cartesianProduct(complexQuestions);
        // Combine the simple questions with the complex questions
        combined = grouped.map((q) => [
          currentQuestionOutput,
          ...flattenedSimpleQuestions,
          ...q.filter(Boolean).flat(),
        ]);
      } else {
        // Just simple
        combined = [[currentQuestionOutput, ...flattenedSimpleQuestions]];
      }

      return combined;
    },
    cartesianProduct(arrays) {
      if (arrays.length === 0) return [[]];
      if (arrays.some((arr) => arr.length === 0)) return [];

      return arrays.reduce(
        (acc, curr) => {
          const result = [];
          acc.forEach((accItem) => {
            curr.forEach((currItem) => {
              result.push([...accItem, currItem].flat(Infinity));
            });
          });
          return result;
        },
        [[]]
      );
    },
  },
  computed: {
    ...mapState(["userProfile"]),
    Questions() {
      if (!this.childQuestions) return [];
      return this.childQuestions.filter((q) => q.parentQuestionId === this.question.formQuestionId);
    },
    Label() {
      if (!this.question) return "";
      return this.question.label + (this.question.required ? " *" : "");
    },
    // Formats FormSubmissionEntryValue object
    Output() {
      let textValue = this.textValue;

      return {
        uuid: this.uuid,
        textValue: textValue,
        // formId: this.formId,
        formQuestionId: this.question && this.question.formQuestionId,
        formQuestionValueId: this.formQuestionValueId,
        required: this.question && !!this.question.required,
        // group: true, //Include group so we don't insert this as an 'answer'
        validated: true, //pass up if validation is more complex than just 'textValue' having a value
      };
    },
    CombinedOutput: function () {
      this.Output;

      return this.formatOutput(this.Output, this.childAnswers);
    },
  },
  watch: {
    childAnswers: {
      deep: true,
      handler(n) {
        const out = this.formatOutput(this.Output, n);
        this.out = out;
        this.$emit("update-responses", out);
      },
    },
    Output: function (n) {
      this.$emit("update-responses", this.formatOutput(n, this.childAnswers));
    },
  },
};
</script>

<style scoped>
.mx-datepicker {
  width: 100%;
}
</style>
<!-- CSS for date picker so it's not scoped -->
<style>
.date-picker .mx-input {
  /* Matches Vuetify border color when inactive */
  border-color: #9e9e9e !important;
  height: 40px !important;
  /* border: 1px solid !important; */
}
.date-picker .mx-input:hover {
  /* Matches Vuetify - black when hovering */
  border-color: black !important;
}
.date-picker .mx-input:focus {
  border: 2px solid !important;
  border-color: var(--v-brandCyan-base) !important;
}
</style>
